/**
 * LE BANDEAU DE FIABILITÉ (lot 108, 18/09/2026).
 *
 * UN SEUL COMPOSANT POUR LES QUATRE ÉCRANS D'ANALYSE. Au lot 106, les quatre
 * routes rendaient déjà « N factures écartées » et aucun écran ne l'affichait :
 * quatre écrans devaient dire la même chose, aucun ne la disait. Un composant
 * partagé, alimenté par une seule route, rend ce genre d'écart impossible.
 *
 * CE QU'IL DIT, depuis le lot 110 (21/09/2026) : les pièces ouvertes dont
 * AUCUNE date de règlement n'est prévue. Elles restent comptées au BFR — elles
 * sont bien dues — mais ne figurent dans aucune semaine de l'échéancier ni du
 * plan de trésorerie. Le bandeau annonce combien, de quel côté, et lesquelles.
 *
 * AU LOT 108 il annonçait autre chose : les pièces échues que le calcul
 * réputait réglées le jour même. Cette invention de date a été supprimée, donc
 * l'alerte a changé d'objet sans changer de rôle.
 *
 * IL NE PARLE PAS POUR NE RIEN DIRE : sans pièce échue ni facture écartée, il
 * ne s'affiche pas. Et si la lecture échoue, il le DIT au lieu de disparaître
 * en silence : un bandeau de fiabilité muet serait une contradiction.
 */

import { useEffect, useState } from 'react';
import { lireFiabilite } from './apiAnalyse.js';
import {
  lignesDe,
  LIBELLES_SENS,
  resumeDe,
  retardLisible,
  type FiabiliteLue,
} from './fiabilite.js';
import { dateFr, euros } from './bfr.js';

export type EcranDuBandeau = 'bfr' | 'echeancier' | 'tresorerie' | 'indicateurs';

export function BandeauFiabilite({
  jeton,
  ecran,
}: {
  readonly jeton: () => Promise<string | null>;
  /** L'écran qui l'affiche : seule la phrase de conséquence en dépend. */
  readonly ecran: EcranDuBandeau;
}) {
  const [donnees, setDonnees] = useState<FiabiliteLue | null>(null);
  const [echoue, setEchoue] = useState(false);

  useEffect(() => {
    let vivant = true;
    void (async () => {
      const j = await jeton();
      if (j === null) return;
      const r = await lireFiabilite(j);
      if (!vivant) return;
      if (r.etat === 'ok') {
        setDonnees(r.valeur);
        setEchoue(false);
      } else {
        setEchoue(true);
      }
    })();
    return () => {
      vivant = false;
    };
  }, [jeton]);

  return <VueBandeauFiabilite donnees={donnees} echoue={echoue} ecran={ecran} />;
}

/**
 * LA VUE, SÉPARÉE DU CHARGEMENT. L'aperçu de maquette monte celle-ci avec des
 * données fixes : au lot 107, une fixture qui écrivait son résultat en dur a
 * continué de mentir après la correction du calcul. Ici la fixture ne fournit
 * que la lecture, et c'est le vrai code d'affichage qui la met en phrase.
 */
export function VueBandeauFiabilite({
  donnees,
  echoue,
  ecran,
}: {
  readonly donnees: FiabiliteLue | null;
  readonly echoue: boolean;
  readonly ecran: EcranDuBandeau;
}) {
  const [ouvert, setOuvert] = useState(false);

  if (echoue) {
    return (
      <p className="bandeau bandeau-attente fiab">
        Le contrôle de fiabilité n’a pas pu être lu. Les chiffres ci-dessous sont
        affichés sans lui.
      </p>
    );
  }

  const resume = resumeDe(donnees);
  if (!resume.aDire) return null;
  const lignes = lignesDe(donnees);

  return (
    <div className="bandeau bandeau-attente fiab">
      <p className="fiab-titre">Ce que le calcul ne prévoit pas</p>
      {resume.echues !== null && (
        <p className="fiab-phrase">
          {resume.echues} <span className="fiab-suite">{CONSEQUENCE[ecran]}</span>
        </p>
      )}
      {resume.ecartees !== null && <p className="fiab-phrase">{resume.ecartees}</p>}
      {/* LE DEUXIÈME VOLET (lot 113) : une PHRASE, sans liste dépliable à lui.
          Le détail est déjà dans Finance › Analyse › Factures, qui compte
          « N sans HT lisible » et permet de retrouver les pièces. Une seconde
          table ici aurait dupliqué un écran qui existe. */}
      {resume.sansHt !== null && (
        <p className="fiab-phrase">
          {resume.sansHt}{' '}
          <span className="fiab-suite">
            Retrouvez-les dans Finance › Analyse › Factures, colonne HT vide.
          </span>
        </p>
      )}
      {lignes.length > 0 && (
        <>
          <button
            type="button"
            className="bouton bouton-discret bouton-petit"
            aria-expanded={ouvert}
            onClick={() => setOuvert(!ouvert)}
          >
            {ouvert
              ? 'Masquer le détail'
              : `Voir les ${String(lignes.length)} pièce${lignes.length > 1 ? 's' : ''}`}
          </button>
          {ouvert && (
            <div className="fiab-liste" role="region" aria-label="Pièces sans date">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Côté</th>
                    <th scope="col">Tiers</th>
                    <th scope="col">Pièce</th>
                    <th scope="col">Échéance</th>
                    <th scope="col" className="fiab-droite">
                      Montant
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l) => (
                    <tr key={`${l.sens}:${l.cle}`}>
                      <td>
                        <span className={`fiab-puce s-${l.sens}`}>
                          {LIBELLES_SENS[l.sens]}
                        </span>
                      </td>
                      <td>
                        {l.tiers_nom}
                        {/* Sous 700 px, les colonnes Pièce et Échéance sont
                            masquées pour que le MONTANT reste à l'écran : le
                            retard, lui, est la raison d'être de la ligne, il
                            revient donc ici. */}
                        <span className="fiab-sur-petit">{retardLisible(l)}</span>
                      </td>
                      <td>{l.numero ?? l.cle}</td>
                      <td>
                        {l.echeance === null ? '—' : dateFr(l.echeance)}
                        <span className="fiab-retard">{retardLisible(l)}</span>
                      </td>
                      <td className="fiab-droite">{euros(l.montant)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="fiab-quoi-faire">
                Pour qu’une de ces pièces rejoigne les prévisions : ouvrez-la dans
                Finance › Analyse › Factures — le filtre Traitement « à dater » les
                réunit — puis fixez sa date de règlement. Si elle ne sera jamais réglée,
                écartez-la des calculs.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * LA CONSÉQUENCE, ÉCRAN PAR ÉCRAN. Le montant est le même partout — c'est le
 * but — mais ce qu'il fausse ne l'est pas, et une phrase vague ne servirait à
 * personne. Chacune de ces phrases décrit un comportement relevé dans le code :
 * `resteA` pour le BFR (une pièce sans part reste due en entier), l'absence de
 * part pour l'échéancier et la trésorerie (`partsDe`, echeancier.ts), et la
 * double origine des indicateurs.
 */
const CONSEQUENCE: Readonly<Record<EcranDuBandeau, string>> = {
  bfr: 'Elles restent comptées au BFR tant qu’elles ne sont pas réglées — elles sont bien dues — mais aucune semaine ne les attend.',
  echeancier:
    'Elles ne figurent donc dans aucune semaine ci-dessous : datez-les pour qu’elles s’y rangent.',
  tresorerie:
    'Aucun mouvement n’est prévu pour elles : le plan est incomplet de ce montant, dans un sens comme dans l’autre.',
  indicateurs:
    'Les indicateurs qui reposent sur les dates prévues ne les voient pas ; ceux qui reposent sur le BFR, si.',
};
